// The three urls that open a show, amend it, and sign a bundle.
//
// Its own module importing nothing, on admin/endpoints.ts's reasoning: the
// console has to name createActivation without pulling Fastify, the signer, or
// freshFactor.ts (and through it `node:crypto`) into a browser build. The urls
// used to live in publish/routes.ts, which was small enough that rateLimit.ts
// imported them from there. That file now proves a second factor on the create
// path, so the import is no longer cheap, and a browser that followed it would
// fail the way console/src/api/admin.ts already failed once on freshFactor.js.

export const PUBLISH_ENDPOINTS = {
  createActivation: "/v1/admin/activations",
  /**
   * Changing what an activation says about itself, after it was opened.
   *
   * A POST that creates an amendment rather than a PATCH on the activation, and
   * the url names the amendment for the same reason. An amendment is a row in
   * `activation_amendment`: it is the record of the change, and it is the thing
   * a superseded opening-hours row points at, so creating one is literally what
   * this route does. A PATCH would also want the activation's id in the path,
   * which is the parameter the flat-path rule above exists to avoid.
   */
  amendActivation: "/v1/admin/activation-amendments",
  /**
   * Recording that a show has finished, or that it has not after all.
   *
   * A CLOSE IS NOT A SEAL AND THIS URL IS NOT THE ONE THAT SEALS. 100 is
   * explicit that `closed_at` says the show finished and does not close writes:
   * a tablet that was offline across the last day comes online later and still
   * syncs everything it holds, and those events are accepted and reported as
   * late rather than dropped. `sealed_at` is the one that ends writes and cannot
   * be undone, it is not set from here, and 106 refuses the publishing role the
   * privilege to write it at all.
   *
   * Which is why this takes a boolean rather than being two urls. Closing and
   * reopening are the same statement with opposite values, both are reversible,
   * and a `/reopen` sitting beside a `/close` would imply a pair of one-way
   * doors. The irreversible thing in this system has its own vocabulary and is
   * deliberately not reachable from this console.
   */
  closeActivation: "/v1/admin/activation-closures",
  /**
   * Pausing a show, or resuming it (151, decision C3).
   *
   * A boolean, on the closure's reasoning: both directions are reversible, so
   * they are one statement with two values rather than a pair of one-way
   * doors. Not a seal and not a close: a paused show takes nothing new from
   * anybody until it is resumed, and keeps everything it has.
   */
  pauseActivation: "/v1/admin/activation-pauses",
  publishBundle: "/v1/admin/config-bundles",
} as const;
